import { EVENT_TEMPLATES, type EventTemplate } from '@eventping/shared'
import { Baby, FilePlus2, Heart, KeyRound, type LucideIcon, Plane, Truck } from 'lucide-react'
import { cn } from '@/lib/utils'

const ICONS: Record<EventTemplate['id'], LucideIcon> = {
  moving: Truck,
  flight: Plane,
  wedding: Heart,
  'home-sale': KeyRound,
  baby: Baby,
}

const chip =
  'inline-flex min-h-10 max-w-full items-center gap-2 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0'

/**
 * "Start from a template": one chip per starter timeline, and optionally "Start blank".
 * `selected` is the picked template's id ('blank' for Start blank), shown pressed.
 */
export function TemplateChips({
  selected,
  onPick,
  withBlank = false,
  disabled = false,
}: {
  selected?: EventTemplate['id'] | 'blank' | null
  onPick: (template: EventTemplate | null) => void
  withBlank?: boolean
  disabled?: boolean
}) {
  const style = (on: boolean) =>
    cn(
      chip,
      on
        ? 'border-primary bg-primary text-primary-foreground'
        : 'bg-background hover:bg-accent hover:text-accent-foreground',
    )
  return (
    <fieldset className="min-w-0">
      <legend className="mb-2 text-sm font-medium">Start from a template</legend>
      <div className="flex flex-wrap gap-2">
        {EVENT_TEMPLATES.map((t) => {
          const Icon = ICONS[t.id]
          return (
            <button
              key={t.id}
              type="button"
              className={style(selected === t.id)}
              aria-pressed={selected === undefined ? undefined : selected === t.id}
              disabled={disabled}
              onClick={() => onPick(t)}
            >
              <Icon aria-hidden />
              <span className="truncate">{t.label}</span>
            </button>
          )
        })}
        {withBlank && (
          <button
            type="button"
            className={style(selected === 'blank')}
            aria-pressed={selected === 'blank'}
            disabled={disabled}
            onClick={() => onPick(null)}
          >
            <FilePlus2 aria-hidden />
            Start blank
          </button>
        )}
      </div>
    </fieldset>
  )
}

/** The picked template's steps, in a line: what Create will add. */
export function TemplatePreview({ template }: { template: EventTemplate }) {
  const loud = template.steps.filter((s) => s.notify).length
  const hidden = template.steps.filter((s) => s.hidden).length
  const note =
    hidden === 0
      ? ''
      : hidden === template.steps.length
        ? ', all hidden until sent'
        : `, ${hidden} hidden until sent`
  return (
    <p className="text-sm text-muted-foreground" data-testid="template-preview">
      {template.steps.length} steps ({loud} notify{note}):{' '}
      {template.steps.map((s) => s.title).join(', ')}. You can edit them after.
    </p>
  )
}
